import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { FormEvent, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AsyncMultiPicker,
  InlineAlert,
  LoadingButton,
} from "src/grading/components";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { ApiError } from "src/models";
import { INVITABLE_ROLES, StaffRole, fullName } from "src/models/staff";
import { useListClassesQuery } from "src/store/reducers/classes/classesApiSlice";
import { useInviteStaffMutation } from "src/store/reducers/invitations/invitationsApiSlice";
import {
  useGetStaffQuery,
  useReactivateStaffMutation,
} from "src/store/reducers/staff/staffApiSlice";
import usePeopleScope from "./usePeopleScope";

export interface InviteStaffDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the email once the invitation is sent. */
  onInvited?: (email: string) => void;
  /** Roles the viewer may grant; platform admin is never offered. */
  grantableRoles: StaffRole[];
  /** Locks the invitation to this district (platform admins). */
  districtId?: string;
  districtName?: string;
  /** Locks the role (the District admins tab). */
  role?: StaffRole;
  /** Dialog title; defaults to "Invite staff". */
  title?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const needsSchool = (role: StaffRole | "") =>
  role === "school_admin" || role === "teacher";

interface ClassOption {
  id: string;
  name: string;
  schoolName: string | null;
}

/**
 * Invites one staff member: email, role, school (school admins and teachers) and classes
 * (teachers). A deactivated account with the same email is offered for reactivation instead.
 */
export const InviteStaffDialog = ({
  open,
  onClose,
  onInvited,
  grantableRoles,
  districtId,
  districtName,
  role: lockedRole,
  title,
}: InviteStaffDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const scope = usePeopleScope(districtId);
  const [inviteStaff, { isLoading: sending }] = useInviteStaffMutation();
  const [reactivateStaff, { isLoading: reactivating }] =
    useReactivateStaffMutation();

  const roles = INVITABLE_ROLES.filter((option) =>
    grantableRoles.includes(option),
  );
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole | "">(lockedRole ?? "");
  const [schoolId, setSchoolId] = useState("");
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reactivateId, setReactivateId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole(lockedRole ?? "");
      setSchoolId("");
      setClasses([]);
      setSubmitted(false);
      setError(null);
      setReactivateId(null);
    }
  }, [open, lockedRole]);

  const { data: existing } = useGetStaffQuery(reactivateId ?? "", {
    skip: !reactivateId,
  });

  const schoolRequired = needsSchool(role);
  const pickSchool = schoolRequired && scope.schools.length > 0;
  const effectiveSchoolId = schoolRequired
    ? schoolId || scope.ownSchoolId || ""
    : "";
  const emailError = submitted && !EMAIL.test(email.trim());
  const roleError = submitted && !role;
  const schoolError = submitted && schoolRequired && !effectiveSchoolId;

  const useClassSearch = (term: string) => {
    const { data, isLoading, isFetching } = useListClassesQuery({
      districtId: scope.requestDistrictId,
      schoolId: effectiveSchoolId || undefined,
      search: term || undefined,
      limit: 25,
    });
    return {
      data: data?.items.map<ClassOption>((item) => ({
        id: item.id,
        name: item.name,
        schoolName: item.schoolName,
      })),
      isLoading,
      isFetching,
    };
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    setSubmitted(true);
    setError(null);
    setReactivateId(null);
    if (!EMAIL.test(email.trim()) || !role) return;
    if (schoolRequired && !effectiveSchoolId) return;
    const address = email.trim();
    try {
      await inviteStaff({
        email: address,
        role,
        districtId,
        schoolId: effectiveSchoolId || undefined,
        classIds:
          role === "teacher" && classes.length
            ? classes.map((item) => item.id)
            : undefined,
      }).unwrap();
      onInvited?.(address);
      onClose();
    } catch (err) {
      const code = refusalCode(err);
      if (code === "REACTIVATE_INSTEAD") {
        const profileId = (err as ApiError).data?.data?.profileId;
        if (typeof profileId === "string") setReactivateId(profileId);
      }
      setError(
        code ? refusalCopy(t, code) : messageOf(err, t("invite.sendError")),
      );
    }
  };

  const reactivate = async () => {
    if (!reactivateId) return;
    try {
      await reactivateStaff(reactivateId).unwrap();
      onClose();
    } catch (err) {
      const code = refusalCode(err);
      setError(
        code
          ? refusalCopy(t, code)
          : messageOf(err, t("invite.reactivateError")),
      );
    }
  };

  const existingName = existing ? fullName(existing) : email.trim();

  return (
    <Dialog
      open={open}
      onClose={sending || reactivating ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={submit} noValidate>
        <DialogTitle id={titleId} component="h2">
          {title ?? t("invite.dialogTitle")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          {districtId ? (
            <TextField
              label={t("invite.district")}
              value={districtName ?? ""}
              InputProps={{ readOnly: true }}
              sx={{ mt: 1 }}
            />
          ) : null}
          <TextField
            label={t("invite.email")}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoFocus
            autoComplete="off"
            error={emailError}
            helperText={emailError ? t("invite.emailInvalid") : undefined}
            inputProps={{ maxLength: 254 }}
            sx={districtId ? undefined : { mt: 1 }}
          />
          <TextField
            select
            label={t("invite.roleLabel")}
            value={role}
            onChange={(event) => {
              setRole(event.target.value as StaffRole);
              if (event.target.value !== "teacher") setClasses([]);
            }}
            required
            disabled={Boolean(lockedRole)}
            error={roleError}
            helperText={roleError ? t("invite.roleRequired") : undefined}
          >
            {(lockedRole ? [lockedRole] : roles).map((option) => (
              <MenuItem key={option} value={option}>
                {t(`roles.${option}`)}
              </MenuItem>
            ))}
          </TextField>
          {pickSchool ? (
            <TextField
              select
              label={t("invite.school")}
              value={schoolId}
              onChange={(event) => {
                setSchoolId(event.target.value);
                setClasses([]);
              }}
              required
              error={schoolError}
              helperText={schoolError ? t("invite.schoolRequired") : undefined}
            >
              {scope.schools.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {schoolError && !pickSchool ? (
            <InlineAlert severity="error">
              {t("invite.schoolRequired")}
            </InlineAlert>
          ) : null}
          {role === "teacher" ? (
            <AsyncMultiPicker<ClassOption>
              label={t("invite.classes")}
              useSearch={useClassSearch}
              getOptionKey={(item) => item.id}
              getOptionLabel={(item) => item.name}
              renderOptionExtra={(item) => item.schoolName}
              value={classes}
              onChange={setClasses}
              helperText={t("invite.classesHelper")}
            />
          ) : null}
          {error ? (
            <InlineAlert
              severity="error"
              action={
                reactivateId ? (
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    color="inherit"
                    loading={reactivating}
                    onClick={reactivate}
                    label={t("invite.reactivate", { name: existingName })}
                  />
                ) : undefined
              }
            >
              {error}
            </InlineAlert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={sending} color="inherit">
            {t("common.cancel")}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={sending}
            label={t("invite.send")}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteStaffDialog;
